import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type { SxProps, Theme } from "@mui/material";
import type { FloatingBannerItem } from "@/features/banner/api/useFloatingBanners";
import {
  useBannerActions,
  type BannerPosition,
} from "@/features/banner/api/useBannerActions";
import {
  isSafeHttpUrl,
  bannerAltText,
  generateImpressionId,
} from "@/utils/bannerUrl";

// レスポンシブ対応のサイズ値（数値・CSS文字列・ブレークポイント別オブジェクト）
type ResponsiveSize = number | string | Record<string, number | string>;

interface AdSlotProps {
  banner: FloatingBannerItem;
  position: BannerPosition;
  /** 画像の描画幅（レスポンシブ可）。 */
  width: ResponsiveSize;
  /** 固定高さ（レクタングル等）。aspectRatio と併用しない。 */
  height?: ResponsiveSize;
  /**
   * 縦横比（例 "728 / 90"）。指定時は width に対して高さを比率で決める。
   * ブレークポイント別も可（例 { xs: "auto", md: "7 / 3" }）。"auto" は画像本来の比率。
   */
  aspectRatio?: string | Record<string, string>;
  /**
   * SP（xs）で画像を実寸（画像px ÷ デバイスピクセル比）以上に引き伸ばさない。
   * 小さい画像はカード中央に等倍で表示する（ぼやけ防止）。md以上は影響しない。
   */
  noUpscaleOnMobile?: boolean;
  /** 画像枠に追加するsx（角丸・境界等） */
  imageSx?: SxProps<Theme>;
  /** カードに見出し・説明文・CTAを表示する（SalesNow風カード）。false時は画像のみ。 */
  showText?: boolean;
  /** 「PR」ラベルを表示する。ピックアップ等は明示、フローティングは非表示（余白削減）。 */
  showPrLabel?: boolean;
  /** カード内側パディング。省略時は既定（テキストカード向け）。フローティングは詰める。 */
  contentPadding?: ResponsiveSize;
  /** CTAリンクの文言（showText時のみ） */
  ctaLabel?: string;
  /** クリック時の追加処理（状態記録など） */
  onClick?: () => void;
}

/**
 * 広告バナー1枠の共通コンポーネント。
 * - 「PR」ラベルを枠外左上に表示し、広告であることを明示（B2B配慮）。
 * - IntersectionObserver で実際に見えたときに impression を1回だけ計測（viewable）。
 *   IO非対応環境ではマウント時に1回計測する。
 * - クリックで click を計測し、リンク先を新規タブで開く。
 * http/https 以外のURLは描画しない（XSS防止）。
 */
export const AdSlot: React.FC<AdSlotProps> = ({
  banner,
  position,
  width,
  height,
  aspectRatio,
  noUpscaleOnMobile = false,
  imageSx,
  showText = false,
  showPrLabel = true,
  contentPadding,
  ctaLabel = "詳しく知る",
  onClick,
}) => {
  const { recordEvent } = useBannerActions();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const impressionIdRef = useRef<string>(generateImpressionId());
  const impressionFiredRef = useRef(false);
  // 画像の実寸（読み込み後に取得）。noUpscaleOnMobile の表示幅上限の計算に使う
  const [naturalWidth, setNaturalWidth] = useState<number | null>(null);
  const [safe] = useState(
    () => isSafeHttpUrl(banner.imageUrl) && isSafeHttpUrl(banner.linkUrl)
  );

  useEffect(() => {
    if (!safe || impressionFiredRef.current) return;

    const fire = (): void => {
      if (impressionFiredRef.current) return;
      impressionFiredRef.current = true;
      void recordEvent(
        banner.id,
        "impression",
        impressionIdRef.current,
        position
      ).catch(() => undefined);
    };

    // IntersectionObserver 非対応環境（テスト等）ではマウント時に計測する
    if (typeof IntersectionObserver === "undefined" || !containerRef.current) {
      fire();
      return;
    }

    const el = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        // 初回通知等では表示面積50%未満でも isIntersecting が真になり得るため、
        // 実際の交差率でも 50% 以上を確認してから impression を計測する。
        if (entries.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5))
          fire();
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
    // banner.id 単位で1回。position/recordEvent は不変扱い。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner.id, safe]);

  if (!safe) return null;

  const handleClick = (): void => {
    void recordEvent(
      banner.id,
      "click",
      impressionIdRef.current,
      position
    ).catch(() => undefined);
    onClick?.();
  };

  return (
    // カード形式: 白背景・角丸・淡いシャドウで「広告カード」として区切る（B2B配慮でPRを明示）。
    // カード全体を1つのリンクにし、画像＋（任意で）見出し・説明・CTAを縦に積む（SalesNow風）。
    <Box
      ref={containerRef}
      component="a"
      href={banner.linkUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        width,
        p: contentPadding ?? { xs: 1.5, md: 2 },
        textDecoration: "none",
        color: "inherit",
        backgroundColor: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        // パディング0（画像のみ表示）のとき、画像を角丸に沿ってフラッシュに収める
        overflow: "hidden",
        boxShadow: "0px 2px 8px -3px rgba(0, 0, 0, 0.18)",
        transition: "box-shadow 0.15s ease",
        "&:hover": { boxShadow: "0px 6px 16px -4px rgba(0, 0, 0, 0.22)" },
      }}
    >
      {showPrLabel && (
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: 10,
            lineHeight: 1.4,
            color: "#9AA0A6",
            mb: 0.5,
          }}
        >
          PR
        </Typography>
      )}
      <Box
        component="img"
        src={banner.imageUrl}
        alt={bannerAltText(banner.linkUrl)}
        ref={(el: HTMLImageElement | null) => {
          // キャッシュ済みで onLoad が走らない場合に備え、描画時点の実寸も拾う
          if (el?.complete && el.naturalWidth > 0)
            setNaturalWidth((prev) => prev ?? el.naturalWidth);
        }}
        onLoad={(e) => {
          setNaturalWidth(e.currentTarget.naturalWidth);
        }}
        sx={{
          width: "100%",
          ...(noUpscaleOnMobile && naturalWidth
            ? {
                maxWidth: {
                  xs: `${naturalWidth / window.devicePixelRatio}px`,
                  md: "none",
                },
                alignSelf: { xs: "center", md: "stretch" },
              }
            : {}),
          ...(aspectRatio ? { aspectRatio } : { height }),
          objectFit: "cover",
          display: "block",
          borderRadius: "8px",
          ...imageSx,
        }}
      />
      {showText && (banner.title || banner.description) && (
        <Box
          sx={{
            px: { xs: 0.5, md: 1 },
            pt: { xs: 1.5, md: 1.75 },
            pb: { xs: 1.25, md: 1.5 },
          }}
        >
          {banner.title && (
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1.5,
                color: "#1F2933",
                // 見出しは常に2行分の高さを確保（全カードの高さを揃える）。超過は「…」省略
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: "3em",
              }}
            >
              {banner.title}
            </Typography>
          )}
          {banner.description && (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12.5,
                lineHeight: 1.7,
                color: "#5F6B76",
                // 説明は常に2行分の高さを確保。超過は「…」省略
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: "3.4em",
              }}
            >
              {banner.description}
            </Typography>
          )}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1.25,
              color: "primary.main",
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}
            >
              {ctaLabel}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
